import {throwError as observableThrowError,  Observable } from 'rxjs';

import {catchError, tap} from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import * as Sentry from "@sentry/angular";

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(private injector: Injector) {
    }
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startTime = Date.now();

        // HTTP 요청 시작 Breadcrumb
        Sentry.addBreadcrumb({
            type: 'http',
            category: 'http.request',
            message: `${request.method} ${request.url}`,
            level: 'info',
            data: {
                method: request.method,
                url: request.url,
                // 민감 정보 제외: Authorization 헤더, Body 등
            },
        });

        return next.handle(request).pipe(
        tap((event: HttpEvent<any>) => {
            // 응답 성공 시 Breadcrumb
            if (event instanceof HttpResponse) {
                const duration = Date.now() - startTime;
                Sentry.addBreadcrumb({
                    type: 'http',
                    category: 'http.response',
                    message: `${request.method} ${request.url} - ${event.status}`,
                    level: 'info',
                    data: {
                        method: request.method,
                        url: request.url,
                        status_code: event.status,
                        duration_ms: duration,
                    },
                });
            }
        }),
        catchError((err: HttpErrorResponse, source) => {
            const duration = Date.now() - startTime;

            // structuredClone은 함수를 복제할 수 없어서 DataCloneError 발생
            // 필요한 속성만 추출하여 새 객체 생성
            let error: any = {
                status: err.status,
                statusText: err.statusText,
                message: err.message,
                error: err.error,
                name: err.name,
                ok: err.ok
            };
            // const logsService = this.injector.get(LogsService);
            const location = this.injector.get(LocationStrategy);
            const url = location instanceof PathLocationStrategy ? location.path() : '';
            error['url'] = url;

            // HTTP 에러 Breadcrumb 추가
            Sentry.addBreadcrumb({
                type: 'http',
                category: 'http.error',
                message: `${request.method} ${request.url} - ${err.status} ${err.statusText}`,
                level: 'error',
                data: {
                    method: request.method,
                    url: request.url,
                    status_code: err.status,
                    status_text: err.statusText,
                    duration_ms: duration,
                    error_message: err.message,
                },
            });

            // 특정 HTTP 에러 코드는 Sentry로 전송 (401, 403 제외)
            if (err.status !== 0 && err.status !== 401 && err.status !== 403 &&
                err.status !== 409 && err.status !== 412) {
                // 심각한 서버 에러는 Sentry에 명시적으로 전송
                // Device proxy 엔드포인트는 504(타임아웃), 500(report_key 없음) 등
                // 운영상 예상되는 에러이므로 Sentry 전송에서 제외
                const isProxyRequest = request.url.includes('/proxy');
                if (err.status >= 500 && !isProxyRequest) {
                    // HttpErrorResponse를 구조화된 Error 객체로 변환하여 Sentry에 전송
                    const httpError = new Error(`HTTP ${err.status} ${err.statusText}: ${request.method} ${request.url}`);
                    httpError.name = 'HttpErrorResponse';
                    Sentry.captureException(httpError, {
                        tags: {
                            http_status: err.status.toString(),
                            http_method: request.method,
                        },
                        contexts: {
                            http_request: {
                                url: request.url,
                                method: request.method,
                                status_code: err.status,
                                error_message: err.message,
                            },
                        },
                    });
                }
                // logsService.err(error['_body'], error, request);
            }
            if (err.error instanceof Error) {
                // A client-side or network error occurred. Handle it accordingly.
                // console.error('An error occurred:', err.error.message);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                // console.error(`Backend returned code ${err.status}, body was: ${err.error}`);
            }

            // ...optionally return a default fallback value so app can continue (pick one)
            // which could be a default value (which has to be a HttpResponse here)
            // return Observable.of(new HttpResponse({body: [{name: "Default value..."}]}));
            // or simply an empty observable
            // return Observable.empty<HttpEvent<any>>();
            return observableThrowError(err);
            // return Observable.empty<HttpEvent<any>>();
        }));
    }
}
